import { Injectable } from "@angular/core"
import { Apollo } from "apollo-angular"
import gql from "graphql-tag"
import { fragments, Post, Customer, Submission} from "./submission.queries"
import { graphqlPatch, graphqlDelete } from "src/graphql/mutation-helper"
import { errorToast } from "src/toaster"
import { map } from "rxjs/operators"

const requestKeywords = gql`
  mutation requestKeywords($input: RequestKeywordsInput!) {
    RequestKeywords(input: $input) {
      errors
      customer {
        id
        canRequestKeywords
      }
    }
  }
`

const updatePost = gql`
  mutation updatePost($input: UpdateCustomerPostMutationInput!) {
    UpdateCustomerPostMutation(input: $input) {
      errors
      post {
        ...SubmissionsPagePost
      }
    }
  }
  ${fragments.post}
`

const updateRating = gql`
  mutation updateRating($input: UpdateCustomerPostRatingMutationInput!) {
    UpdateCustomerPostRatingMutation(input: $input) {
      errors
      post {
        ...SubmissionsPagePost
      }
    }
  }
  ${fragments.post}
`

const updateCustomer = gql`
  mutation updateCustomer($input: UpdateCustomerMutationInput!) {
    UpdateCustomerMutation(input: $input) {
      errors
      customer {
        ...SubmissionsPageCustomer
      }
    }
  }
  ${fragments.customer}
`

const updateSubmission = gql`
  mutation updateSubmission($input: UpdateCustomerSubmissionMutationInput!) {
    UpdateCustomerSubmissionMutation(input: $input) {
      errors
      submission {
        ...SubmissionsPageSubmission
      }
    }
  }
  ${fragments.submission}
`

const purchaseSubmission = gql`
  mutation purchaseSubmission($input: PurchaseCustomerSubmissionMutationInput!) {
    PurchaseCustomerSubmissionMutation(input: $input) {
      errors
      submission {
        ...SubmissionsPageSubmission
      }
    }
  }
  ${fragments.submission}
`

const redeliverSubmission = gql`
  mutation redeliverSubmission($input: RedeliverSubmissionInput!) {
    RedeliverSubmission(input: $input) {
      errors
      submission {
        ...SubmissionsPageSubmission
      }
    }
  }
  ${fragments.submission}
`

const purchaseExtra = gql`
  mutation purchaseExtra($input: PurchaseExtraInput!) {
    PurchaseExtra(input: $input) {
      errors
      extraPurchase {
        id
        post {
          id
          bpsEvents {
            id
            task {
              id
              code
            }
          }
        }
      }
    }
  }
`

@Injectable({providedIn: 'root'})
export class SubmissionMutations {

  constructor(private apollo: Apollo) {}

  togglePostExpanded({ id, expanded }: { id: number, expanded: boolean }): Promise<void> {
    return new Promise((resolve, reject) => {
      this.apollo.mutate({
        mutation: gql`
          mutation togglePostExpanded($id: Int, $expanded: Boolean) {
            TogglePostExpandedMutation(id: $id, expanded: $expanded) @client
          }
        `,
        variables: {
          id,
          expanded
        }
      }).subscribe(({ data }) => resolve())
    })
  }

  updatePost({ post, updates, transition, activities }: { post: Post, updates?: Partial<Post>, transition?: string, activities?: string[] }): Promise<Post> {
    return new Promise((resolve, reject) => {
      this.apollo.mutate({
        mutation: updatePost,
        variables: {
          input: {
            customerId: post.customerId,
            id: post.id,
            transition,
            activities,
            patch: graphqlPatch(updates),
            delete: graphqlDelete(updates)
          }
        }
      }).pipe(map((result: any) => result.data.UpdateCustomerPostMutation)).subscribe(({ post, errors }) => {
        if (errors.length > 0) {
          errors.map(e => errorToast(e))
          reject()
        } else if (post) {
          resolve(post)
        } else {
          errorToast('Unknown error updating post')
          reject()
        }
      })
    })
  }

  updateRating({ customerId, id, updates }: { customerId: number, id: number, updates: { value: number } }): Promise<Post> {
    return new Promise((resolve, reject) => {
      this.apollo.mutate({
        mutation: updateRating,
        variables: {
          input: {
            customerId,
            id,
            patch: graphqlPatch(updates),
            delete: graphqlDelete(updates)
          }
        }
      }).pipe(map((result: any) => result.data.UpdateCustomerPostRatingMutation)).subscribe(({ post, errors }) => {
        if (errors.length > 0) {
          errors.map(e => errorToast(e))
          reject()
        } else if (post) {
          resolve(post)
        } else {
          errorToast('Unknown error updating rating')
          reject()
        }
      })
    })
  }

  updateCustomer({id, updates}: { id: number, updates: Partial<Customer> }): Promise<Customer> {
    return new Promise((resolve, reject) => {
      this.apollo.mutate({
        mutation: updateCustomer,
        variables: {
          input: {
            id,
            patch: graphqlPatch(updates),
            delete: graphqlDelete(updates)
          }
        }
      }).pipe(map((result: any) => result.data.UpdateCustomerMutation)).subscribe(({ customer, errors }) => {
        if (errors.length > 0) {
          errors.forEach(e => errorToast(e))
          reject()
        } else if (customer) {
          resolve(customer)
        }
      })
    })
  }

  updateSubmissionUi({ submission, updates }: { submission: Submission, updates: object }): Promise<void> {
    return new Promise((resolve, reject) => {
      this.apollo.mutate({
        mutation: gql`
          mutation UpdateSubmissionUi($id: Int, $updates: any) {
            UpdateSubmissionUi(id: $id, updates: $updates) @client
          }
        `,
        variables: {
          id: submission.id,
          updates
        }
      }).subscribe(({ data, errors }) => {
        resolve()
      })
    })
  }

  updateSubmission({ submission, updates }: { submission: Submission, updates: object }): Promise<Submission> {
    return new Promise((resolve, reject) => {
      this.apollo.mutate({
        mutation: updateSubmission,
        variables: {
          input: {
            id: submission.id,
            patch: graphqlPatch(updates),
            delete: graphqlDelete(updates)
          }
        }
      }).pipe(map((result: any) => result.data.UpdateCustomerSubmissionMutation)).subscribe(({ submission, errors }) => {
        if (errors.length > 0) {
          errors.forEach(e => errorToast(e))
          reject()
        }
        else if (submission) {resolve(submission)}
      })
    })
  }

  purchaseSubmission({ submission }: { submission: Submission }): Promise<Submission> {
    return new Promise((resolve, reject) => {
      this.apollo.mutate({
        mutation: purchaseSubmission,
        variables: {
          input: {
            id: submission.id
          }
        }
      }).pipe(map((result: any) => result.data.PurchaseCustomerSubmissionMutation)).subscribe(({ errors, submission }) => {
        if (errors.length > 0) {
          errors.forEach(e => errorToast(e))
          reject()
        }
        else if (submission) {resolve(submission)}
      })
    })
  }

  redeliverSubmission({submission}: {submission: Submission}): Promise<Submission> {
    return new Promise((resolve, reject) => {
      this.apollo.mutate({
        mutation: redeliverSubmission,
        variables: {
          input: {
            id: submission.id
          }
        }
      }).pipe(map((result: any) => result.data.RedeliverSubmission)).subscribe(({errors, submission}) => {
        if (errors.length > 0) {
          errors.forEach(e => errorToast(e))
          reject()
        }
        else if (submission)
          resolve(submission)
      })
    })
  }

  purchasePostExtra({ postId, code }: { postId: number, code: string }): Promise<any> {
    return new Promise((resolve, reject) => {
      this.apollo.mutate({
        mutation: purchaseExtra,
        variables: {
          input: {
            extraPurchaseSpec: {
              extrableId: postId, extrableType: 'Post', extraCode: code
            }
          }
        }
      }).pipe(map((result: any) => result.data.PurchaseExtra)).subscribe( ({errors, extraPurchase}) => {
        if (errors.length > 0) {
          errors.forEach(e => errorToast(e))
          reject()
        }
        else if (extraPurchase) resolve(extraPurchase)
      })
    })
  }

  requestKeywords({customerId}: {customerId: number}): Promise<any> {
    return new Promise((resolve, reject) => {
      this.apollo.mutate({
        mutation: requestKeywords,
        variables: {
          input: {
            customerId
          }
        }
      }).pipe(map((result: any) => result.data.RequestKeywords)).subscribe(({ errors, customer }) => {
        if (errors.length > 0) {
          errors.forEach(e => errorToast(e))
          reject()
        } else if (customer) resolve(customer)
      })
    })
  }
}
